import { Session } from '@supabase/supabase-js';
import { InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { changeLanguage } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';

import { studentBookingClient } from 'src/api';
import { appConfig } from 'src/app/app-config';
import { AuthStateEvent } from 'src/auth/enum/AuthStateEvent';
import { useInitializeSupabase } from 'src/auth/hooks/use-initialize-supabase';
import { supabaseClient } from 'src/auth/supabase';
import { Loader } from 'src/layout/loader';
import { getTheme } from 'src/utils/getTheme';

import { AuthContext } from './auth-context';
import { UserLoginState } from './types/supabase-types';

export const SupabaseProvider: FC<PropsWithChildren> = ({ children }) => {
    const ACCESS_TOKEN_KEY = appConfig.supabase.cookies.accessTokenKey as string;
    const REFRESH_TOKEN_KEY = appConfig.supabase.cookies.refreshTokenKey as string;
    const VS_COOKIE_DOMAIN = appConfig.supabase.cookies.domain;
    const HOS_COOKIE_DOMAIN = appConfig.supabase.cookies.hosDomain;

    const bearerRequestInterceptorId = useRef<number | null>(null);
    const [loggedInState, setLoggedInState] = useState<UserLoginState>({
        loggedIn: false,
        session: null,
    });

    const { isIdle, isPending, mutate: initializeSupabase } = useInitializeSupabase();
    const queryClient = useQueryClient();

    const { isHosTheme } = getTheme();

    const cookieDomain = isHosTheme ? HOS_COOKIE_DOMAIN : VS_COOKIE_DOMAIN;

    const setSessionCookies = (session: Session | null) => {
        Cookies.set(ACCESS_TOKEN_KEY, session?.access_token ?? '', {
            domain: cookieDomain,
            secure: true,
            sameSite: 'Lax',
            expires: 7,
        });
        Cookies.set(REFRESH_TOKEN_KEY, session?.refresh_token ?? '', {
            domain: cookieDomain,
            secure: true,
            sameSite: 'Lax',
            expires: 7,
        });
    };

    const removeSessionCookies = () => {
        Cookies.remove(ACCESS_TOKEN_KEY, { domain: cookieDomain });
        Cookies.remove(REFRESH_TOKEN_KEY, { domain: cookieDomain });
    };

    useEffect(() => {
        initializeSupabase();

        const { data: authListener } = supabaseClient.auth.onAuthStateChange(
            async (_event, session) => {
                switch (_event) {
                    case AuthStateEvent.INITIAL_SESSION: {
                        const accessToken = Cookies.get(ACCESS_TOKEN_KEY);
                        const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);

                        const { error } = await supabaseClient.auth.getUser(accessToken);
                        const isValidToken = !error;

                        if (accessToken && refreshToken && isValidToken) {
                            supabaseClient.auth.setSession({
                                access_token: accessToken,
                                refresh_token: refreshToken,
                            });
                        }
                        break;
                    }
                    case AuthStateEvent.SIGNED_IN:
                    case AuthStateEvent.TOKEN_REFRESHED:
                    case AuthStateEvent.USER_UPDATED:
                        setSessionCookies(session);
                        setLoggedInState({ loggedIn: true, session: session as Session });
                        break;

                    case AuthStateEvent.SIGNED_OUT:
                        removeSessionCookies();
                        setLoggedInState({ loggedIn: false, session: null });
                        queryClient.clear();
                        break;

                    default:
                        break;
                }

                if (bearerRequestInterceptorId.current) {
                    studentBookingClient.interceptors.request.eject(
                        bearerRequestInterceptorId.current
                    );
                    bearerRequestInterceptorId.current = null;
                }

                if (!session) return;

                const interceptorId = studentBookingClient.interceptors.request.use(
                    (config: InternalAxiosRequestConfig) => {
                        config.headers.Authorization = `Bearer ${session.access_token}`;

                        return config;
                    }
                );

                bearerRequestInterceptorId.current = interceptorId;

                if (session.user.user_metadata.preferredLanguage)
                    changeLanguage(session.user.user_metadata.preferredLanguage);
            }
        );

        return () => authListener?.subscription.unsubscribe();
    }, []);

    if (isPending || isIdle) return <Loader />;

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <AuthContext.Provider value={loggedInState}>{children}</AuthContext.Provider>;
};
